// src/components/DashRankingInsumos.jsx
import React, { useState, useMemo } from "react";
import { supabase } from "../supabaseClient";
import "./DashRankingInsumos.css";

function DashRankingInsumos({ projectId }) {
  const [insumosRanking, setInsumosRanking] = useState([]);
  const [insumoBusca, setInsumoBusca] = useState("");
  const [insumoModalAberto, setInsumoModalAberto] = useState(null);
  const [insumoDetalhes, setInsumoDetalhes] = useState(null);
  const [insumoDetalhesLoading, setInsumoDetalhesLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const insumosFiltrados = useMemo(() => {
    const q = insumoBusca.trim();
    if (!q) return insumosRanking;

    const norm = (s) =>
      (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const qNorm = norm(q);

    if (qNorm.startsWith("%")) {
      const partes = qNorm.split("%").filter(Boolean);
      return insumosRanking.filter((ins) => {
        const haystack = norm(ins.descricao);
        let pos = 0;
        for (const parte of partes) {
          const idx = haystack.indexOf(parte, pos);
          if (idx === -1) return false;
          pos = idx + parte.length;
        }
        return true;
      });
    }

    return insumosRanking.filter((ins) => {
      const haystack = norm(ins.descricao);
      const words = haystack.split(/\s+/);
      return words.some((w) => w.startsWith(qNorm));
    });
  }, [insumosRanking, insumoBusca]);

  const abrirInsumoModal = async (insumo) => {
    setInsumoModalAberto(insumo);
    setInsumoDetalhes(null);
    setInsumoDetalhesLoading(true);
    
    try {
      let allItens = [];
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;
      
      while (hasMore) {
        const from = page * pageSize;
        const to = from + pageSize - 1;
        
        const { data, error } = await supabase
          .from("planilha_itens")
          .select("codigo, descricao, unidade, quantidade, locacao")
          .eq("projeto_id", projectId)
          .eq("codigo", insumo.codigo)
          .range(from, to);
        
        if (error) break;
        
        if (data && data.length > 0) {
          allItens = [...allItens, ...data];
        }
        
        hasMore = data && data.length === pageSize;
        page++;
        if (page > 10) break;
      }
      
      const extrairLocacoes = (locacao) => {
        if (!locacao) return [];
        
        try {
          if (typeof locacao === 'string' && locacao.startsWith('[')) {
            const parsed = JSON.parse(locacao);
            if (Array.isArray(parsed)) return parsed;
          }
          return [locacao];
        } catch (e) {
          return [locacao];
        }
      };
      
      const porLocalizacao = new Map();
      let totalGeral = 0;
      
      allItens.forEach(item => {
        const qtdBase = parseFloat(String(item.quantidade || "0").replace(",", ".")) || 0;
        const locacoes = extrairLocacoes(item.locacao);
        
        if (locacoes.length === 0) {
          const chave = "Sem localização";
          if (!porLocalizacao.has(chave)) {
            porLocalizacao.set(chave, { local: chave, quantidade: 0 });
          }
          porLocalizacao.get(chave).quantidade += qtdBase;
          totalGeral += qtdBase;
        } else {
          locacoes.forEach(local => {
            const localLimpo = local.toString().trim();
            if (!porLocalizacao.has(localLimpo)) {
              porLocalizacao.set(localLimpo, { local: localLimpo, quantidade: 0 });
            }
            porLocalizacao.get(localLimpo).quantidade += qtdBase;
            totalGeral += qtdBase;
          });
        }
      });
      
      const detalhes = Array.from(porLocalizacao.values())
        .sort((a, b) => b.quantidade - a.quantidade);
      
      setInsumoDetalhes({
        codigo: insumo.codigo,
        descricao: insumo.descricao,
        unidade: insumo.unidade,
        totalGeral: totalGeral,
        porLocalizacao: detalhes
      });
      
    } catch (err) {
      // erro silencioso
    } finally {
      setInsumoDetalhesLoading(false);
    }
  };

  const fecharInsumoModal = () => {
    setInsumoModalAberto(null);
    setInsumoDetalhes(null);
  };

  // Buscar dados dos insumos
  React.useEffect(() => {
    if (!projectId) {
      setInsumosRanking([]);
      setLoading(false);
      return;
    }
    
    (async () => {
      setLoading(true);
      try {
        let allItens = [];
        let page = 0;
        const pageSize = 1000;
        let hasMore = true;
        
        while (hasMore) {
          const from = page * pageSize;
          const to = from + pageSize - 1;
          
          const { data, error } = await supabase
            .from("planilha_itens")
            .select("codigo, descricao, unidade, quantidade, locacao")
            .eq("projeto_id", projectId)
            .range(from, to);
          
          if (error) {
            break;
          }
          
          if (data && data.length > 0) {
            allItens = [...allItens, ...data];
          }
          
          hasMore = data && data.length === pageSize;
          page++;
          
          if (page > 10) break;
        }
        
        if (allItens.length === 0) {
          setInsumosRanking([]);
          setLoading(false);
          return;
        }
        
        const contarLocacoes = (locacao) => {
          if (!locacao) return 1;
          
          try {
            let locacoesArray = [];
            
            if (typeof locacao === 'string') {
              if (locacao.startsWith('[')) {
                try {
                  const parsed = JSON.parse(locacao);
                  if (Array.isArray(parsed)) {
                    locacoesArray = parsed;
                  }
                } catch (e) {
                  locacoesArray = [locacao];
                }
              } else {
                locacoesArray = [locacao];
              }
            } else if (Array.isArray(locacao)) {
              locacoesArray = locacao;
            }
            
            const count = locacoesArray.length;
            
            return Math.max(1, count);
          } catch (err) {
            return 1;
          }
        };
        
        const agrupado = new Map();
        
        allItens.forEach(item => {
          const codigo = item.codigo?.trim();
          if (!codigo || codigo === "" || codigo.toLowerCase() === "criar") return;
          
          const chave = codigo;
          if (!agrupado.has(chave)) {
            agrupado.set(chave, {
              codigo: codigo,
              descricao: item.descricao || "Sem descrição",
              unidade: item.unidade || "-",
              total: 0
            });
          }
          
          let qtdBase = 0;
          if (item.quantidade) {
            const strQtd = String(item.quantidade).replace(",", ".");
            qtdBase = parseFloat(strQtd);
            if (isNaN(qtdBase)) qtdBase = 0;
          }
          
          const multiplicador = contarLocacoes(item.locacao);
          const qtdTotal = qtdBase * multiplicador;
          
          const itemAgrupado = agrupado.get(chave);
          itemAgrupado.total += qtdTotal;
        });
        
        const ranking = Array.from(agrupado.values())
          .filter(item => item.total > 0)
          .sort((a, b) => b.total - a.total);
        
        setInsumosRanking(ranking);
        
      } catch (err) {
        setInsumosRanking([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  return (
    <>
      <div className="dash-ranking-insumos">
        <div className="dash-ranking-header">
          <span className="dash-ranking-title">RANKING DE INSUMOS</span>
          <input
            type="text"
            value={insumoBusca}
            onChange={(e) => setInsumoBusca(e.target.value)}
            placeholder="%cimento  ou  %tub%PVC"
            className="dash-ranking-search"
          />
        </div>
        
        <div className="dash-ranking-table-wrapper">
          <table className="dash-ranking-table">
            <thead>
              <tr>
                <th className="dash-ranking-col-codigo">Código</th>
                <th className="dash-ranking-col-descricao">Descrição</th>
                <th className="dash-ranking-col-unidade">Unid.</th>
                <th className="dash-ranking-col-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="dash-ranking-loading">
                    Carregando insumos...
                  </td>
                </tr>
              ) : insumosFiltrados.map((ins, i) => (
                <tr 
                  key={`${ins.codigo}-${i}`} 
                  className="dash-ranking-row"
                  onClick={() => abrirInsumoModal(ins)}
                >
                  <td className="dash-ranking-codigo">{ins.codigo}</td>
                  <td className="dash-ranking-descricao">{ins.descricao}</td>
                  <td className="dash-ranking-unidade">{ins.unidade}</td>
                  <td className="dash-ranking-total">
                    {ins.total.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                  </td>
                </tr>
              ))}
              {!loading && insumosFiltrados.length === 0 && (
                <tr>
                  <td colSpan={4} className="dash-ranking-empty">
                    {insumoBusca ? "Nenhum resultado para a busca" : "Nenhum insumo registrado nas listagens"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {insumosFiltrados.length > 0 && !loading && (
          <div className="dash-ranking-footer">
            <span className="dash-ranking-count">
              {insumosFiltrados.length} insumo{insumosFiltrados.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* Modal de detalhamento do insumo */}
      {insumoModalAberto && (
        <div className="dash-ranking-modal-overlay" onClick={fecharInsumoModal}>
          <div className="dash-ranking-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-ranking-modal-header">
              <div>
                <span className="dash-ranking-modal-badge">
                  {insumoModalAberto.codigo}
                </span>
                <h3 className="dash-ranking-modal-title">
                  {insumoModalAberto.descricao}
                </h3>
              </div>
              <button 
                type="button" 
                className="dash-ranking-modal-close"
                onClick={fecharInsumoModal}
              >
                ×
              </button>
            </div>
            
            <div className="dash-ranking-modal-body">
              {insumoDetalhesLoading ? (
                <p className="dash-ranking-modal-loading">
                  Carregando...
                </p>
              ) : insumoDetalhes ? (
                <>
                  <div className="dash-ranking-modal-total">
                    <div className="dash-ranking-modal-total-label">TOTAL GERAL</div>
                    <div className="dash-ranking-modal-total-value">
                      {insumoDetalhes.totalGeral.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                      <span className="dash-ranking-modal-total-unit">{insumoDetalhes.unidade}</span>
                    </div>
                  </div>
                  
                  <div className="dash-ranking-modal-subtitle">
                    DETALHAMENTO POR LOCALIZAÇÃO
                  </div>
                  
                  <div className="dash-ranking-modal-list">
                    {insumoDetalhes.porLocalizacao.map((item, idx) => {
                      const percentual = (item.quantidade / insumoDetalhes.totalGeral) * 100;
                      return (
                        <div key={idx} className="dash-ranking-modal-item">
                          <div className="dash-ranking-modal-item-info">
                            <span className="dash-ranking-modal-item-local">
                              {item.local}
                            </span>
                            <div className="dash-ranking-modal-item-bar">
                              <div 
                                className="dash-ranking-modal-item-bar-fill"
                                style={{ width: `${percentual}%` }}
                              />
                            </div>
                          </div>
                          <div className="dash-ranking-modal-item-quantidade">
                            <span className="dash-ranking-modal-item-number">
                              {item.quantidade.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </span>
                            <span className="dash-ranking-modal-item-unit">
                              {insumoDetalhes.unidade}
                            </span>
                            <div className="dash-ranking-modal-item-percent">
                              {percentual.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="dash-ranking-modal-footer">
                    {insumoDetalhes.porLocalizacao.length} localização(ões) · Total calculado com base em todas as listagens do projeto
                  </div>
                </>
              ) : (
                <p className="dash-ranking-modal-empty">
                  Nenhum dado encontrado para este insumo.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DashRankingInsumos;